import classNames from 'classnames';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import Typography, {
  type TypographyStyle,
} from '@/components/atoms/typography/typography';
import styles from './comma-separated-list.module.scss';

export interface CommaSeparatedListProps {
  /** The class name to apply */
  className?: string;
  /** The content to show */
  list: string[];
  /** The typography styling to be used */
  tagStyle?: TypographyStyle;
}

const MIN_CONTAINER_WIDTH = 250;

const getSeparator = (visibleCount: number) => {
  if (visibleCount === 0) return '';

  if (visibleCount === 1) return ' ';

  return ', ';
};

const formatVisibleItems = (items: string[]) => {
  if (items.length <= 1) return items.join('');

  return items.reduce((result, item, index) => {
    if (index === 0) {
      return item;
    }

    return `${result}${getSeparator(index)}${item}`;
  }, '');
};

const CommaSeparatedList = ({
  className,
  list = [],
  tagStyle = 'bodyMedium',
  ...rest
}: CommaSeparatedListProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState<null | number>(null);

  // Parents often pass a new array with the same content on every render, so key the
  // measuring effect on the content instead of the array reference
  const listKey = JSON.stringify(list);
  const items = useMemo<string[]>(() => JSON.parse(listKey), [listKey]);

  // A layout effect measures before paint, so a new list never flashes with the old count
  useLayoutEffect(() => {
    let lastVisibleCount: null | number = null;

    // Always remeasure: a resize can change the result without changing the width
    // (responsive font sizes, a hidden container being shown)
    const calculateVisibleItems = () => {
      if (!containerRef.current) {
        return;
      }

      const container = containerRef.current;
      // Measure inside the text element so the measurement uses the rendered font
      const textElement = container.firstElementChild ?? container;
      // Fractional widths, as offsetWidth rounds to whole pixels
      const measuredWidth = container.getBoundingClientRect().width;
      const containerWidth =
        measuredWidth === 0 ? MIN_CONTAINER_WIDTH : measuredWidth;
      let fitCount = 0;

      const temporarySpan = document.createElement('span');

      temporarySpan.style.visibility = 'hidden';
      temporarySpan.style.position = 'absolute';
      temporarySpan.style.whiteSpace = 'nowrap';
      textElement.appendChild(temporarySpan);

      // Measure each candidate as the full rendered text, as summing separately measured
      // pieces drops the leading space of the first separator and adds up rounding
      for (let count = 1; count <= items.length; count++) {
        const remainingCount = items.length - count;
        const suffix = remainingCount > 0 ? `, +${remainingCount}` : '';

        temporarySpan.innerText = `${formatVisibleItems(items.slice(0, count))}${suffix}`;

        if (temporarySpan.getBoundingClientRect().width > containerWidth) {
          break;
        }

        fitCount = count;
      }

      textElement.removeChild(temporarySpan);

      // Always show the first item, truncated with an ellipsis when it doesn't fit
      const nextVisibleCount = Math.max(fitCount, Math.min(items.length, 1));

      // Only update when the result changed, so a resize can't trigger a re-render on its own
      if (nextVisibleCount === lastVisibleCount) {
        return;
      }

      lastVisibleCount = nextVisibleCount;
      setVisibleCount(nextVisibleCount);
    };

    calculateVisibleItems();

    const resizeObserver = new ResizeObserver(calculateVisibleItems);

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    // A web font swapping in changes text widths without resizing the container
    const fonts = 'fonts' in document ? document.fonts : null;

    fonts?.addEventListener('loadingdone', calculateVisibleItems);

    return () => {
      resizeObserver.disconnect();
      fonts?.removeEventListener('loadingdone', calculateVisibleItems);
    };
    // tagStyle changes the font, which can change text widths without resizing the container
  }, [items, tagStyle]);

  const visibleItems =
    visibleCount === null ? [] : items.slice(0, visibleCount);
  const remainingCount =
    visibleCount === null ? 0 : items.length - visibleCount;

  return (
    <div
      ref={containerRef}
      {...rest}
      className={classNames(styles.component, className)}
    >
      <Typography className={styles.itemsContainer} tagStyle={tagStyle}>
        <span className={styles.items}>{formatVisibleItems(visibleItems)}</span>

        {remainingCount > 0 && (
          <span className={styles.remaining}>{`, +${remainingCount}`}</span>
        )}
      </Typography>
    </div>
  );
};

CommaSeparatedList.displayName = 'CommaSeparatedList';

export default CommaSeparatedList;
